import { __assign, __spreadArrays } from "tslib";
import { DEFAULT_LIST_DROPDOWN_CONFIG } from './list-dropdown-constants';
import { isListDropdownOptionType, ListDropdownOptionType } from './list-dropdown-utils';
import { POPUP_DIALOG_WIDTH_THRESHOLD } from '../constants';
var ListDropdownFoundation = /** @class */ (function () {
    function ListDropdownFoundation(_adapter, config) {
        var _this = this;
        this._adapter = _adapter;
        this._open = false;
        this._config = Object.assign(__assign({}, DEFAULT_LIST_DROPDOWN_CONFIG), __assign({}, config));
        this._selectListener = function (value, id) { return _this._onSelect(value, id); };
        this._closeListener = function () {
            if (typeof _this._config.closeCallback === 'function') {
                _this._config.closeCallback();
            }
        };
        this._scrollEndListener = function () {
            if (_this._config.observeScroll && typeof _this._config.scrollEndListener === 'function') {
                _this._config.scrollEndListener();
            }
        };
    }
    ListDropdownFoundation.prototype.destroy = function () {
        if (this._open) {
            this.close();
        }
    };
    ListDropdownFoundation.prototype.open = function () {
        if (!this._open) {
            this._open = true;
            var dialogWidthThreshold = typeof this._config.dialogWidthThreshold === 'number' ? this._config.dialogWidthThreshold : POPUP_DIALOG_WIDTH_THRESHOLD;
            var isMobile = this._adapter.getScreenWidth() <= dialogWidthThreshold;
            this._adapter.open(this._config, this._selectListener, isMobile, this._closeListener);
            this.activateInitialOption();
            if (this._config.observeScroll && this._config.scrollEndListener) {
                this._adapter.setScrollBottomListener(this._scrollEndListener, this._config.observeScrollThreshold || 0);
            }
        }
    };
    ListDropdownFoundation.prototype.close = function () {
        if (this._open) {
            this._open = false;
            this._adapter.close();
            if (this._config.observeScroll && this._config.scrollEndListener) {
                this._adapter.removeScrollBottomListener(this._scrollEndListener);
            }
        }
    };
    ListDropdownFoundation.prototype._onSelect = function (value, id) {
        if (typeof this._config.activeChangeCallback === 'function') {
            this._config.activeChangeCallback(id);
        }
        if (typeof this._config.selectCallback === 'function') {
            this._config.selectCallback(value);
        }
        if (this._open) {
            var sync = this._config.syncWidth === undefined ? true : false;
            // Keep the popup width at least the same as the target if the target size has now changed
            this._adapter.syncWidth(sync);
        }
    };
    ListDropdownFoundation.prototype.getActiveOptionIndex = function () {
        return this._adapter.getActiveOptionIndex();
    };
    ListDropdownFoundation.prototype.toggleOptionMultiple = function (index, isSelected) {
        return this._adapter.toggleOptionMultiple(index, isSelected);
    };
    ListDropdownFoundation.prototype.activateSelectedOption = function () {
        this._adapter.activateSelectedOption(this._config);
    };
    ListDropdownFoundation.prototype.activateFirstOption = function () {
        var index = this._nonDividerOptions.findIndex(function (o) { return !o.disabled; });
        if (index !== -1) {
            this.activateOption(index);
        }
        return index;
    };
    ListDropdownFoundation.prototype.activateLastOption = function () {
        var options = this._nonDividerOptions;
        var index = (options.length - 1) - options.findIndex(function (o) { return !o.disabled; });
        if (index !== -1) {
            this.activateOption(index);
        }
        return index;
    };
    ListDropdownFoundation.prototype.activateOption = function (index, animate) {
        return this._adapter.activateOption(index, this._config.activeChangeCallback, animate);
    };
    ListDropdownFoundation.prototype.activateInitialOption = function () {
        if (typeof this._config.activeStartIndex === 'number' && this._nonDividerOptions[this._config.activeStartIndex]) {
            this.activateOption(this._config.activeStartIndex, false);
            this._adapter.scrollSelectedOptionIntoView(false);
        }
        else if (this._config.selectedValues && this._config.selectedValues.length) {
            this._adapter.activateSelectedOption(this._config);
            this._adapter.scrollSelectedOptionIntoView(false);
        }
        else if (this._config.highlightFirst && this._config.options.length) {
            this.activateFirstOption();
        }
    };
    ListDropdownFoundation.prototype.setSelectedValues = function (values) {
        if (!Array.isArray(values)) {
            values = [values];
        }
        if (!this._config.multiple && values.length > 1) {
            values = [values[0]];
        }
        this._config.selectedValues = values;
        this._adapter.setSelectedValues(values, this._config.multiple);
    };
    ListDropdownFoundation.prototype.clearActiveOption = function () {
        this._adapter.clearActiveOption();
    };
    ListDropdownFoundation.prototype.setOptions = function (options) {
        this._config.options = options;
        if (!this._open) {
            return;
        }
        this._adapter.setOptions(this._config);
        this.activateInitialOption();
    };
    ListDropdownFoundation.prototype.appendOptions = function (options) {
        this._config.options = __spreadArrays(this._config.options, options);
        if (!this._open) {
            return;
        }
        this._adapter.appendOptions(options, this._config);
    };
    Object.defineProperty(ListDropdownFoundation.prototype, "dropdownElement", {
        get: function () {
            return this._adapter.dropdownElement;
        },
        enumerable: true,
        configurable: true
    });
    ListDropdownFoundation.prototype.scrollSelectedOptionIntoView = function (animate) {
        if (animate === void 0) { animate = true; }
        this._adapter.scrollSelectedOptionIntoView(animate);
    };
    ListDropdownFoundation.prototype.setScrollBottomListener = function (listener, threshold) {
        this._config.scrollEndListener = listener;
        this._config.observeScroll = true;
        this._config.observeScrollThreshold = threshold;
        this._adapter.setScrollBottomListener(this._scrollEndListener, this._config.observeScrollThreshold || 0);
    };
    ListDropdownFoundation.prototype.removeScrollBottomListener = function () {
        this._config.scrollEndListener = undefined;
        this._adapter.removeScrollBottomListener(this._scrollEndListener);
    };
    ListDropdownFoundation.prototype.setBusyVisibility = function (isVisible) {
        if (!this._config.allowBusy) {
            return;
        }
        this._adapter.setBusyVisibility(isVisible);
    };
    ListDropdownFoundation.prototype.handleKey = function (key) {
        switch (key) {
            case 'Enter':
                var activeOptionIndex = this.getActiveOptionIndex();
                var activeOption = this._nonDividerOptions[activeOptionIndex];
                if (activeOption) {
                    var id = this._adapter.getActiveOptionIdByIndex(activeOptionIndex);
                    if (id) {
                        this._onSelect(activeOption.value, id);
                    }
                }
                break;
            case 'Up':
            case 'ArrowUp':
            case 'Down':
            case 'ArrowDown':
                var index = this._getNextActiveOptionIndex(key);
                this.activateOption(index);
                break;
            case 'Home':
                this.activateFirstOption();
                break;
            case 'End':
                this.activateLastOption();
                break;
        }
    };
    ListDropdownFoundation.prototype._getNextActiveOptionIndex = function (key) {
        var index = this._adapter.getActiveOptionIndex();
        if (index === -1) {
            return this.activateFirstOption();
        }
        if (key === 'ArrowUp' || key === 'Up') {
            return this._getPreviousHighlightableOptionIndex(index, this._nonDividerOptions);
        }
        return this._getNextHighlightableOptionIndex(index, this._nonDividerOptions);
    };
    ListDropdownFoundation.prototype._getPreviousHighlightableOptionIndex = function (startIndex, options) {
        var index = startIndex <= 0 ? options.length - 1 : startIndex - 1;
        if (options[index].disabled) {
            return this._getPreviousHighlightableOptionIndex(index, options);
        }
        return index;
    };
    ListDropdownFoundation.prototype._getNextHighlightableOptionIndex = function (startIndex, options) {
        var index = startIndex === options.length - 1 ? 0 : startIndex + 1;
        if (options[index].disabled) {
            return this._getNextHighlightableOptionIndex(index, options);
        }
        return index;
    };
    Object.defineProperty(ListDropdownFoundation.prototype, "_flatOptions", {
        get: function () {
            if (isListDropdownOptionType(this._config.options, ListDropdownOptionType.Group)) {
                var groups = this._config.options;
                return groups.reduce(function (flatOpts, group) { return flatOpts.concat(group.options); }, []);
            }
            return __spreadArrays(this._config.options);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ListDropdownFoundation.prototype, "_nonDividerOptions", {
        get: function () {
            return this._flatOptions.filter(function (o) { return !o.divider; });
        },
        enumerable: true,
        configurable: true
    });
    return ListDropdownFoundation;
}());
export { ListDropdownFoundation };
